import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { withdrawalAPI, bookieAPI, authAPI } from '../services/api';
import './ClubFund.css';

function ClubWithdrawals() {
  const { clubId } = useParams();
  const [withdrawals, setWithdrawals] = useState([]);
  const [pendingWithdrawal, setPendingWithdrawal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState('');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [balances, setBalances] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  // Preview state
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [wData, cData, bData, uData] = await Promise.all([
        withdrawalAPI.list(clubId),
        withdrawalAPI.current(clubId).catch(() => ({ withdrawal: null })),
        bookieAPI.getBookieBalance(clubId).catch(() => null),
        authAPI.getCurrentUser().catch(() => null),
      ]);
      setWithdrawals(wData.withdrawals || []);
      setPendingWithdrawal(cData.withdrawal);
      if (bData?.success) setBalances(bData.balances);
      if (uData?.user) setCurrentUser(uData.user);
    } catch (err) { /* ignore */ }
    setLoading(false);
  }, [clubId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handlePreview = async (e) => {
    e.preventDefault();
    setPreviewing(true);
    setResult(null);
    try {
      const data = await withdrawalAPI.preview(clubId, parseFloat(amount));
      if (data.success) {
        setPreview(data.preview);
        setShowPreview(true);
      } else {
        setResult({ success: false, message: data.error });
      }
    } catch (err) {
      setResult({ success: false, message: err.response?.data?.error || 'Preview failed' });
    }
    setPreviewing(false);
  };

  const handleCreate = async () => {
    setSubmitting(true);
    setResult(null);
    try {
      const data = await withdrawalAPI.create(clubId, parseFloat(amount), comment);
      setResult({ success: true, message: data.message });
      setAmount('');
      setComment('');
      setShowPreview(false);
      setPreview(null);
      loadData();
    } catch (err) {
      setResult({ success: false, message: err.response?.data?.error || 'Failed' });
    }
    setSubmitting(false);
  };

  const handleProcess = async (id) => {
    if (!window.confirm('Process this withdrawal? Funds will be transferred from the club.')) return;
    try {
      await withdrawalAPI.process(id);
      loadData();
    } catch (err) { alert(err.response?.data?.error || 'Failed to process'); }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this withdrawal request?')) return;
    try {
      await withdrawalAPI.cancel(id);
      loadData();
    } catch (err) { alert(err.response?.data?.error || 'Failed to cancel'); }
  };

  const handleRespond = async (id, approve) => {
    const action = approve ? 'approve' : 'deny';
    if (!window.confirm(`Are you sure you want to ${action} this withdrawal?`)) return;
    try {
      await withdrawalAPI.respond(id, approve);
      loadData();
    } catch (err) { alert(err.response?.data?.error || `Failed to ${action}`); }
  };

  const fmtCurrency = (v) => '$' + Number(v || 0).toFixed(2);

  const isCaptain = currentUser && pendingWithdrawal?.captain?.userId === currentUser.id;

  // Check if current user has a pending response on a withdrawal
  const getUserResponse = (withdrawal) => {
    if (!currentUser || !withdrawal.responses) return null;
    return withdrawal.responses.find(r => r.userId === currentUser.id);
  };

  return (
    <div>
      <h2>Club Withdrawals</h2>

      {balances && (
        <div className="balance-cards">
          <div className="balance-card kitty">
            <div className="balance-label">Kitty</div>
            <div className="balance-amount">${balances.kitty.dollars.toFixed(2)}</div>
          </div>
          <div className="balance-card betting">
            <div className="balance-label">Betting</div>
            <div className="balance-amount">${balances.betting.dollars.toFixed(2)}</div>
          </div>
        </div>
      )}

      {/* Pending Withdrawal Banner for Members */}
      {pendingWithdrawal && !isCaptain && (() => {
        const myResponse = getUserResponse(pendingWithdrawal);
        if (!myResponse || myResponse.status !== 'pending') return null;
        return (
          <div style={{ background: 'rgba(251,191,36,0.15)', border: '1px solid rgba(251,191,36,0.3)', borderRadius: '8px', padding: '1rem', marginBottom: '1rem' }}>
            <div style={{ color: '#fbbf24', fontWeight: 700, marginBottom: '0.5rem' }}>Withdrawal Pending Your Approval</div>
            <p style={{ color: '#ccc', fontSize: '0.85rem', margin: '0.25rem 0' }}>
              {pendingWithdrawal.captain?.name || 'The captain'} has proposed a {fmtCurrency(pendingWithdrawal.amount)} withdrawal.
              Your share: <strong style={{ color: '#fff' }}>{fmtCurrency(myResponse.amount)}</strong> ({(parseFloat(myResponse.equity) * 100).toFixed(1)}% equity)
            </p>
            {pendingWithdrawal.comment && <p style={{ color: '#999', fontSize: '0.8rem', fontStyle: 'italic' }}>"{pendingWithdrawal.comment}"</p>}
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
              <button onClick={() => handleRespond(pendingWithdrawal.id, true)} style={{ flex: 1, background: '#10b981', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.6rem', cursor: 'pointer', fontWeight: 600 }}>Approve</button>
              <button onClick={() => handleRespond(pendingWithdrawal.id, false)} style={{ flex: 1, background: '#ef4444', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.6rem', cursor: 'pointer', fontWeight: 600 }}>Deny</button>
            </div>
          </div>
        );
      })()}

      {/* Preview Modal */}
      {showPreview && preview && (
        <div className="fund-form">
          <h3 style={{ color: '#fff', marginBottom: '0.75rem' }}>Withdrawal Preview</h3>
          <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '0.75rem', marginBottom: '0.75rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', color: '#ccc', fontSize: '0.85rem', marginBottom: '0.25rem' }}>
              <span>Withdrawal Amount</span>
              <span style={{ color: '#fff', fontWeight: 700 }}>{fmtCurrency(preview.amount)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', color: '#ccc', fontSize: '0.85rem', marginBottom: '0.25rem' }}>
              <span>Available Balance</span>
              <span>{fmtCurrency(preview.balance.total / 100)}</span>
            </div>
            {!preview.withinWageredLimit && (
              <div style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '0.5rem' }}>
                Exceeds wagered limit ({fmtCurrency((preview.wageredLimitCents || 0) / 100)})
              </div>
            )}
          </div>

          <div style={{ fontSize: '0.7rem', color: '#666', marginBottom: '0.5rem', textTransform: 'uppercase', fontWeight: 600 }}>Member Equity Split</div>
          {preview.members.map((m, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.85rem', color: '#999', padding: '0.4rem 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
              <span style={{ color: '#ccc' }}>{m.name || 'Member'}</span>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <span style={{ color: '#666', fontSize: '0.75rem' }}>{(m.equity * 100).toFixed(1)}%</span>
                <span style={{ color: '#fff', fontWeight: 600 }}>{fmtCurrency(m.amount)}</span>
              </div>
            </div>
          ))}

          <div className="form-group" style={{ marginTop: '0.75rem' }}>
            <label>Comment (optional)</label>
            <input type="text" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Reason for withdrawal" className="dark-input" style={{ width: '100%', background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', padding: '0.75rem', boxSizing: 'border-box' }} />
          </div>

          {result && <div className={`result-message ${result.success ? 'success' : 'error'}`}>{result.message}</div>}

          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
            <button onClick={handleCreate} className="btn-submit" disabled={submitting} style={{ flex: 1 }}>
              {submitting ? 'Creating...' : 'Confirm Withdrawal'}
            </button>
            <button onClick={() => { setShowPreview(false); setPreview(null); }} style={{ flex: 0.5, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#999', borderRadius: '8px', padding: '0.75rem', cursor: 'pointer' }}>
              Back
            </button>
          </div>
        </div>
      )}

      {/* Create Form (hidden when preview is showing) */}
      {!showPreview && (
        <form onSubmit={handlePreview} className="fund-form">
          <h3 style={{ color: '#fff', marginBottom: '1rem' }}>New Withdrawal</h3>
          <div className="form-group">
            <label>Amount ($)</label>
            <div className="amount-input-group">
              <span className="currency-symbol">$</span>
              <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} min="1" step="0.01" required placeholder="0.00" />
            </div>
          </div>
          {result && !showPreview && <div className={`result-message ${result.success ? 'success' : 'error'}`}>{result.message}</div>}
          <button type="submit" className="btn-submit" disabled={previewing || !amount}>
            {previewing ? 'Calculating...' : 'Preview Withdrawal'}
          </button>
        </form>
      )}

      {/* Withdrawal History */}
      <div className="recent-transactions">
        <h3>Withdrawal History</h3>
        {loading ? <p style={{ color: '#666' }}>Loading...</p> :
          withdrawals.length === 0 ? <p className="no-transactions">No withdrawals yet</p> :
          withdrawals.map((w) => (
            <div key={w.id} className="dark-card" style={{ background: 'rgba(0,0,0,0.25)', borderRadius: '8px', padding: '1rem', marginBottom: '0.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ color: '#fff', fontWeight: 700 }}>{fmtCurrency(w.amount)}</span>
                <span style={{ color: w.status === 'actioned' ? '#10b981' : w.status === 'pending' ? '#fbbf24' : w.status === 'canceled' ? '#666' : '#ef4444', fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase' }}>{w.status}</span>
              </div>
              {w.comment && <p style={{ color: '#999', fontSize: '0.85rem', margin: '0.25rem 0' }}>{w.comment}</p>}
              <p style={{ color: '#666', fontSize: '0.75rem' }}>{new Date(w.created).toLocaleDateString()}</p>

              {/* Member Equity Breakdown */}
              {w.responses?.length > 0 && (
                <div style={{ marginTop: '0.75rem', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '0.5rem' }}>
                  <div style={{ fontSize: '0.7rem', color: '#666', marginBottom: '0.25rem', textTransform: 'uppercase', fontWeight: 600 }}>Member Equity Breakdown</div>
                  {w.responses.map((r) => {
                    const isMe = currentUser && r.userId === currentUser.id;
                    return (
                      <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.8rem', color: '#999', padding: '0.3rem 0', borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                        <span style={{ color: isMe ? '#fff' : '#ccc' }}>{r.name}{isMe ? ' (you)' : ''}</span>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                          {r.equity && <span style={{ color: '#666', fontSize: '0.7rem' }}>{(parseFloat(r.equity) * 100).toFixed(1)}%</span>}
                          <span style={{ color: '#fff', fontWeight: 600 }}>{fmtCurrency(r.amount)}</span>
                          <span style={{
                            padding: '1px 6px', borderRadius: 3, fontSize: '0.65rem', fontWeight: 600,
                            background: r.status === 'approved' ? '#065f46' : r.status === 'denied' ? '#7f1d1d' : '#92400e',
                            color: r.status === 'approved' ? '#10b981' : r.status === 'denied' ? '#ef4444' : '#fbbf24'
                          }}>{r.status}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Member Approve/Deny for pending withdrawals */}
              {w.status === 'pending' && (() => {
                const myResponse = getUserResponse(w);
                if (myResponse && myResponse.status === 'pending') {
                  return (
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                      <button onClick={() => handleRespond(w.id, true)} style={{ flex: 1, background: '#10b981', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.5rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>Approve My Share</button>
                      <button onClick={() => handleRespond(w.id, false)} style={{ flex: 1, background: '#ef4444', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.5rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>Deny</button>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Captain Controls */}
              {w.status === 'pending' && currentUser && w.captain?.userId === currentUser.id && (
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                  <button onClick={() => handleProcess(w.id)} style={{ flex: 1, background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)', color: '#10b981', borderRadius: '6px', padding: '0.4rem', cursor: 'pointer', fontSize: '0.8rem' }}>Process</button>
                  <button onClick={() => handleCancel(w.id)} style={{ flex: 1, background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', borderRadius: '6px', padding: '0.4rem', cursor: 'pointer', fontSize: '0.8rem' }}>Cancel</button>
                </div>
              )}
            </div>
          ))
        }
      </div>
    </div>
  );
}

export default ClubWithdrawals;
